import { authApi } from './api';

/**
 * Shape of the error payloads this API returns. Axios wraps them in
 * `error.response.data`, and `unknown` is the honest type for a caught value,
 * so this narrows it without reaching for `any`.
 */
interface ApiErrorBody {
  message?: string;
  restart?: boolean;
}

function apiError(error: unknown): ApiErrorBody {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: unknown } }).response;
    const data = response?.data;
    if (typeof data === 'object' && data !== null) return data as ApiErrorBody;
  }
  return {};
}

/**
 * Two-factor login client for the Lynki portal.
 *
 * Two-factor authentication is mandatory on the Lynki platform (see docs/SECURITY-2FA.md).
 * The portal normally delegates login to the central Lynki.Auth site, which enforces the
 * same flow, but these calls back the inline login form used in development and when the
 * portal is hosted as the primary entry point.
 *
 * Flow:
 *   1. `login`            verifies the password and triggers a one-time code by email
 *   2. `verifyTwoFactor`  exchanges the code for a session cookie
 */
export interface LoginResult {
  success: boolean;
  /**
   * Normally true when success is true: the code entry step is required.
   * Can be false if Lynki.Auth already trusts this browser (a valid
   * `lynki_trusted_device` cookie) and the session is established already —
   * only an explicit `false` from the API should skip the code entry step.
   */
  requiresTwoFactor: boolean;
  /** Partially masked destination address, e.g. "he****@lynki.no". */
  maskedEmail?: string;
  /** Validity of the code in minutes, for display. */
  expiresInMinutes?: number;
  /**
   * Whether to offer "husk denne enheten" on the code step. False when the admin policy
   * has turned the feature off, or this account is an admin that must always type a code.
   */
  canRememberDevice?: boolean;
  /** Admin-configured trust window, for the checkbox label. */
  trustedDeviceLifetimeDays?: number;
  message: string;
}

/** The fields of a login/2FA-status response that describe the remember-device offer. */
export interface RememberDeviceOffer {
  canRememberDevice?: boolean;
  trustedDeviceLifetimeDays?: number;
}

function rememberDeviceOffer(data: unknown): RememberDeviceOffer {
  const d = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;
  return {
    // Only an explicit false hides the option: an older API that omits the field still
    // supports rememberDevice on the 2FA request.
    canRememberDevice: d.canRememberDevice !== false,
    trustedDeviceLifetimeDays:
      typeof d.trustedDeviceLifetimeDays === 'number' ? d.trustedDeviceLifetimeDays : undefined,
  };
}

export interface TwoFactorResult {
  success: boolean;
  message: string;
  /** True when the challenge expired and the user must re-enter their password. */
  restart?: boolean;
}

export const twoFactorService = {
  /** Step 1: verify the password and trigger the emailed one-time code. */
  async login(email: string, password: string, rememberMe = false): Promise<LoginResult> {
    try {
      const { data } = await authApi.post('/api/user/login', { email, password, rememberMe });
      return {
        success: true,
        requiresTwoFactor: data?.requiresTwoFactor !== false,
        maskedEmail: data?.maskedEmail,
        expiresInMinutes: data?.expiresInMinutes,
        ...rememberDeviceOffer(data),
        message: data?.message || '',
      };
    } catch (error: unknown) {
      const body = apiError(error);
      return {
        success: false,
        requiresTwoFactor: false,
        message: body.message || 'Innlogging feilet. Prøv igjen.',
      };
    }
  },

  /**
   * Step 2: exchange the emailed one-time code for a session.
   *
   * `rememberDevice` asks Lynki.Auth to also issue a `lynki_trusted_device` cookie so
   * this browser can skip the code step for the next 90 days. The next `login` call
   * then comes back with `requiresTwoFactor: false` and the session already made.
   */
  async verifyTwoFactor(code: string, rememberMe = false, rememberDevice = false): Promise<TwoFactorResult> {
    try {
      await authApi.post('/api/user/login/2fa', { code, rememberMe, rememberDevice });
      return { success: true, message: '' };
    } catch (error: unknown) {
      const body = apiError(error);
      return {
        success: false,
        message: body.message || 'Ugyldig eller utløpt engangskode.',
        restart: body.restart === true,
      };
    }
  },

  /** Requests a fresh one-time code. Rate limited server-side. */
  async resendCode(): Promise<TwoFactorResult> {
    try {
      const { data } = await authApi.post('/api/user/login/2fa/resend');
      return { success: true, message: data?.message || 'Ny kode sendt.' };
    } catch (error: unknown) {
      const body = apiError(error);
      return {
        success: false,
        message: body.message || 'Kunne ikke sende ny kode.',
        restart: body.restart === true,
      };
    }
  },

  /**
   * Checks whether a two-factor challenge is still pending for this browser,
   * so a page refresh can resume code entry instead of restarting the login.
   */
  async getStatus(): Promise<{ pending: boolean; maskedEmail?: string } & RememberDeviceOffer> {
    try {
      const { data } = await authApi.get('/api/user/login/2fa/status');
      return { pending: data?.pending === true, maskedEmail: data?.maskedEmail, ...rememberDeviceOffer(data) };
    } catch {
      return { pending: false };
    }
  },
};

export default twoFactorService;
